import React, { useCallback, useEffect, useState } from 'react';
import MobileModal from '../../../../components/compositions/MobileModal/MobileModal';
import strings from '../../../../constants/strings.constants';
import {
  cancelReservation,
  editReservationDate,
  fetchReservationHistoryDetail,
  sendReservationCompleteMessage,
} from '../../../../apis/reservations';
import useAuthStore from '../../../../store/useAuthStore';
import Spinner from '../../../../components/atom/Spinner';
import { Fade } from 'react-awesome-reveal';
import colors from '../../../../constants/colors.constants';
import styled from 'styled-components';
import Typo from '../../../../components/atom/Typo';
import moment from 'moment';
import {
  formatCurrency,
  formatTelNumber,
  getPartnerName,
  getProductOptionTotalPrice,
  isValidReservationPushChannel,
} from '../../../../utils/utils';
import Button from '../../../../components/atom/Button';
import useAlertStore from '../../../../store/useAlertStore';
import AppError from '../../../../apis/models/common/res/AppError';
import { renderReservationTag } from '../../../../utils/render-utils';
import { ReservationStatus } from '../../../../apis/models/reservations/res/Reservation';
import ReservationDateSelectModal from './ReservationDateSelectModal';
import { ReservationDetail } from '../../../../apis/models/reservations/res/ReservationDetail';
import { useSearchParams } from 'react-router-dom';
import useLanguageCodeStore from '../../../../store/useLanguageCodeStore';
import { ChatbotChannel } from '../../../../apis/models/auth/req/ChatbotGuestSignInBody';
import { sendRefundEvent } from '../../../../utils/ga-utils';

export interface ReservationDetailModalProps {
  visible?: boolean;
  onCancel?: () => void;
  reservationId?: number;
}

function ReservationDetailModal(props: ReservationDetailModalProps) {
  const { visible, onCancel, reservationId } = props;
  const { showAlert } = useAlertStore();
  const [requesting, setRequesting] = useState(false);
  const [fetching, setFetching] = useState(false);
  const { customer, partner } = useAuthStore();
  const [dateSelectModalVisible, setDateSelectModalVisible] = useState(false);
  const [detail, setDetail] = useState<ReservationDetail>();
  const [searchParams] = useSearchParams();
  const { languageCode } = useLanguageCodeStore();
  const customLabel =
    partner?.productViewConfig?.mimoWebViewConfig?.customLabel;

  useEffect(() => {
    if (visible) {
      (async function fetchData() {
        await requestFetchReservationDetail();
      })();
    }
  }, [visible, customer]);

  const isKoreanLang = languageCode === 'ko' || !languageCode;

  const requestFetchReservationDetail = async () => {
    setFetching(true);
    try {
      if (customer && reservationId) {
        const row = await fetchReservationHistoryDetail(
          customer.token,
          reservationId,
        );
        setDetail(row);
      }
    } catch (e) {
    } finally {
      setFetching(false);
    }
  };

  const requestEditReservationDate = async (reservedAt: string) => {
    try {
      setRequesting(true);
      if (reservationId && customer) {
        let reservationPushChannel: ChatbotChannel | undefined;
        let respondIOUserKey: string | undefined;
        const chatbotChannel =
          searchParams.get('chatbotChannel') ||
          searchParams.get('chatBotChannel') ||
          undefined;
        if (isValidReservationPushChannel(chatbotChannel)) {
          reservationPushChannel = chatbotChannel as ChatbotChannel;
          const userKey = searchParams.get('userKey') || undefined;
          if (chatbotChannel === ChatbotChannel.RESPOND_IO && userKey) {
            respondIOUserKey = userKey;
          }
        }
        await editReservationDate(
          customer.token,
          reservationId,
          reservedAt,
          languageCode !== 'ko' ? languageCode : undefined,
          reservationPushChannel,
          respondIOUserKey,
        );
        showAlert(strings.completeEditReservationDate);
        await requestFetchReservationDetail();
        const chatLogId = searchParams.get('chatLogId') || undefined;
        if (chatLogId && partner) {
          try {
            const chatId = parseInt(chatLogId);
            sendReservationCompleteMessage(partner.id, customer.token, {
              reservationId,
              chatLogId: chatId,
              reservationType: 'MODIFY',
            }).then(() => {
              // Do nothing
            });
          } catch (e) {
            console.error(e);
            // Do nothing
          }
        }
      }
    } catch (e) {
      const error = e as AppError;
      if (error.message) {
        showAlert(error.message);
      }
    } finally {
      setRequesting(false);
    }
  };

  const requestCancelReservation = async () => {
    try {
      setRequesting(true);
      if (reservationId && customer) {
        let reservationPushChannel: ChatbotChannel | undefined;
        let respondIOUserKey: string | undefined;
        const chatbotChannel =
          searchParams.get('chatbotChannel') ||
          searchParams.get('chatBotChannel') ||
          undefined;
        if (isValidReservationPushChannel(chatbotChannel)) {
          reservationPushChannel = chatbotChannel as ChatbotChannel;
          const userKey = searchParams.get('userKey') || undefined;
          if (chatbotChannel === ChatbotChannel.RESPOND_IO && userKey) {
            respondIOUserKey = userKey;
          }
        }

        await cancelReservation(
          customer.token,
          reservationId,
          languageCode !== 'ko' ? languageCode : undefined,
          reservationPushChannel,
          respondIOUserKey,
        );

        sendRefundEvent(
          reservationId,
          getProductOptionTotalPrice(detail?.reservationProductOptions ?? []),
          detail?.reservationProductOptions ?? [],
        );

        showAlert(strings.completeReservationCancel);
        await requestFetchReservationDetail();
        const chatLogId = searchParams.get('chatLogId') || undefined;
        if (chatLogId && partner) {
          try {
            const chatId = parseInt(chatLogId);
            sendReservationCompleteMessage(partner.id, customer.token, {
              reservationId,
              chatLogId: chatId,
              reservationType: 'CANCEL',
            }).then(() => {
              // Do nothing
            });
          } catch (e) {
            console.error(e);
            // Do nothing
          }
        }
      }
    } catch (e) {
      const error = e as AppError;
      if (error.message) {
        showAlert(error.message);
      }
    } finally {
      setRequesting(false);
    }
  };

  const renderDetail = useCallback(() => {
    if (detail) {
      const {
        tel,
        name,
        reservedAt,
        reservationMemo,
        status,
        reservationProductOptions = [],
        treatmentCategoryName,
        treatmentSubCategoryName,
      } = detail;

      let categoryText = '';
      if (treatmentCategoryName) {
        categoryText += treatmentCategoryName;
      }
      if (treatmentSubCategoryName) {
        categoryText += ` / ${treatmentSubCategoryName}`;
      }
      console.log(
        treatmentCategoryName,
        treatmentSubCategoryName,
        categoryText,
      );

      let reservationDateText = moment(reservedAt)
        .tz('Asia/Seoul')
        .format('YYYY.MM.DD. (dd) a hh:mm');

      if (languageCode !== 'ko') {
        reservationDateText = moment(reservedAt)
          .tz('Asia/Seoul')
          .format('YYYY.MM.DD. (dd) a hh:mm [(KST)]');
      }

      let partnerDisplayName = getPartnerName(partner);
      let memoText = strings.noConsultingMemo;
      if (reservationMemo) {
        memoText = reservationMemo;
      }

      return (
        <div className="flex flex-col gap-3">
          {/* 예약일 */}
          <Section>
            <Typo type="b-18">{strings.reservationDate}</Typo>
            <CardWrapper>
              <div className="flex flex-col gap-2">
                <div>{renderReservationTag(reservedAt, status)}</div>
                <Typo type="b-18">{reservationDateText}</Typo>
                <Typo type="r-16">{partnerDisplayName}</Typo>
              </div>
            </CardWrapper>
          </Section>

          {/* 진료분야 */}
          {categoryText && isKoreanLang && (
            <Section>
              <Typo type="b-18">{strings.treatmentCategoryTitle}</Typo>
              <CardWrapper>
                <Typo type="r-16">{categoryText}</Typo>
              </CardWrapper>
            </Section>
          )}

          {/* 선택 시술 */}
          {reservationProductOptions.length > 0 && (
            <Section>
              <div className="flex justify-between">
                <Typo type="b-18">{strings.selectedTreatments}</Typo>
                <Typo type="r-16">{`${reservationProductOptions.length}${strings.unitEA}`}</Typo>
              </div>
              <div className="flex flex-col gap-2">
                {reservationProductOptions.map(
                  ({
                    id,
                    productOptionName,
                    productOptionDescription = '',
                    productOptionTotalDiscountPrice,
                    productOptionTotalPrice,
                  }) => {
                    let productOptionOriginPrice: number | undefined;
                    let productOptionPrice = productOptionTotalPrice;
                    if (productOptionTotalDiscountPrice) {
                      productOptionPrice = productOptionTotalDiscountPrice;
                    }
                    if (productOptionTotalDiscountPrice) {
                      productOptionOriginPrice = productOptionTotalPrice;
                    }

                    let discountRateText = '';
                    if (
                      productOptionTotalDiscountPrice &&
                      productOptionTotalPrice
                    ) {
                      const discountRate = Math.round(
                        100 -
                          (productOptionTotalDiscountPrice /
                            productOptionTotalPrice) *
                            100,
                      );
                      discountRateText = `${discountRate}%`;
                    }

                    return (
                      <CardWrapper key={id}>
                        <div className="flex flex-col">
                          <Typo type="b-14">{productOptionName}</Typo>
                          {productOptionDescription && (
                            <div className="mt-1">
                              <Typo type="r-12" color={colors.subTitle2}>
                                {productOptionDescription}
                              </Typo>
                            </div>
                          )}
                          {productOptionOriginPrice && (
                            <div className="mt-1">
                              <Typo
                                style={{
                                  textDecoration: 'line-through',
                                }}
                                type="r-12"
                                color={colors.subTitle}
                              >
                                {`${formatCurrency(productOptionOriginPrice)} ${strings.priceUnit}`}
                              </Typo>
                            </div>
                          )}
                          <div className="flex items-center gap-1 mt-1">
                            {discountRateText && (
                              <Typo type="b-14" color={colors.red2}>
                                {discountRateText}
                              </Typo>
                            )}
                            <Typo
                              type="b-14"
                              color={colors.subTitle}
                            >{`${formatCurrency(productOptionPrice)} ${strings.priceUnit}`}</Typo>
                          </div>
                        </div>
                      </CardWrapper>
                    );
                  },
                )}
              </div>
            </Section>
          )}

          {/*  예약자 정보*/}
          <Section>
            <div className="flex justify-between">
              <Typo type="b-18">{strings.reservationCustomerInfo}</Typo>
            </div>
            <div className="flex flex-col gap-4 mt-2">
              <div className="flex justify-between">
                <Typo type="r-16" color={colors.subTitle}>
                  {strings.reservationCustomerName}
                </Typo>
                <Typo type="r-16">{name}</Typo>
              </div>
              <div className="flex justify-between">
                <Typo type="r-16" color={colors.subTitle}>
                  {strings.reservationCustomerPhone}
                </Typo>
                <Typo type="r-16">{formatTelNumber(tel)}</Typo>
              </div>
              <div className="flex flex-col justify-between gap-4">
                <Typo
                  type="r-16"
                  color={colors.subTitle}
                  style={{
                    flexShrink: 0,
                  }}
                >
                  {customLabel?.consultingMemo || strings.consultingMemo}
                </Typo>
                <div className="border p-2 rounded-md min-h-[80px]">
                  <Typo
                    type="r-14"
                    style={{
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {memoText}
                  </Typo>
                </div>
              </div>
            </div>
          </Section>
        </div>
      );
    }
    return <div />;
  }, [detail, partner]);

  const renderFooter = () => {
    if (detail && detail.status !== ReservationStatus.Cancel) {
      return (
        <FooterWrapper>
          <div className="flex gap-2 justify-evenly">
            <div className="flex-1">
              <Button
                disabled={requesting}
                type="gray"
                style={{
                  width: '100%',
                }}
                onClick={() => {
                  showAlert(
                    strings.confirmCancelReservation,
                    async () => {
                      await requestCancelReservation();
                    },
                    {
                      showCancelButton: true,
                    },
                  );
                }}
              >
                {strings.cancelReservation}
              </Button>
            </div>
            <div className="flex-1">
              <Button
                disabled={requesting}
                style={{
                  width: '100%',
                }}
                onClick={() => {
                  setDateSelectModalVisible(true);
                }}
              >
                {strings.editReservation}
              </Button>
            </div>
          </div>
        </FooterWrapper>
      );
    }
  };

  return (
    <MobileModal
      visible={visible}
      onCancel={onCancel}
      title={strings.reservationDetail}
      footer={renderFooter()}
    >
      {fetching ? (
        <div className="h-full flex flex-col items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div
          className="flex flex-col h-full overflow-y-auto"
          style={{
            background: colors.greyHover,
          }}
        >
          <Fade duration={200}>{renderDetail()}</Fade>
        </div>
      )}
      <ReservationDateSelectModal
        visible={dateSelectModalVisible}
        reservationId={reservationId}
        onCancel={() => {
          setDateSelectModalVisible(false);
        }}
        onDateSelect={async (reservedAt) => {
          await requestEditReservationDate(reservedAt);
        }}
      />
    </MobileModal>
  );
}

export default ReservationDetailModal;

const Section = styled.div`
  padding: 24px 20px;
  background: white;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CardWrapper = styled.div`
  padding: 14px 16px;
  background: ${colors.greyHover};
  border-radius: 12px;
`;

const FooterWrapper = styled.div`
  position: relative;
  box-shadow: 0px -4px 30px 0px #0000001f;
  background: white;
  padding: 20px;
`;
